// src/components/Inventory/charts/HorizontalBarChart.jsx
import React, { memo, useMemo } from "react";
import { Box } from "@mui/material";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Cell,
  Tooltip as RTooltip,
} from "recharts";
import { faLayerGroup } from "@fortawesome/free-solid-svg-icons";
import ChartCard from "../ChartCard";
import { chartPalette } from "./chartPalette";
import { getTooltipProps } from "./tooltipProps";

function HorizontalBarChart({
  data,
  darkMode,
  title,
  subtitle,
  icon = faLayerGroup,
  iconColor = "green",
  minHeight = 320,
  chartKey,
  t,
  unit = "lbs"
}) {
  const axisColor = darkMode ? "rgba(255,255,255,0.55)" : "rgba(0,0,0,0.55)";
  const gridColor = darkMode ? "rgba(255,255,255,0.10)" : "rgba(0,0,0,0.08)";

  const d = useMemo(() => (Array.isArray(data) ? data.slice(0, 10) : []), [data]);
  const computedHeight = Math.max(minHeight, 90 + d.length * 34);

  const unitLabel = t ? t(unit, unit) : unit;
  const totalLabel = t ? t("Total") : "Total";

  return (
    <ChartCard
      icon={icon}
      iconColor={iconColor}
      title={title}
      badge={d.length > 0 ? `${d.length} ${t ? t("items") : "items"}` : null}
    >
      <Box className="inv-chart-wrap" sx={{ height: computedHeight, mt: 1 }}>
        <ResponsiveContainer key={chartKey} width="100%" height="100%">
          <BarChart data={d} layout="vertical" margin={{ top: 6, right: 18, left: 12, bottom: 6 }}>
            <CartesianGrid stroke={gridColor} strokeDasharray="3 3" />
            <XAxis
              type="number"
              tick={{ fontSize: 12, fill: axisColor }}
              axisLine={{ stroke: axisColor }}
              tickLine={{ stroke: axisColor }}
            />
            <YAxis
              type="category"
              dataKey="name"
              width={170}
              tick={{ fontSize: 12, fill: axisColor }}
              axisLine={{ stroke: axisColor }}
              tickLine={{ stroke: axisColor }}
              tickFormatter={(v) => (String(v).length > 22 ? String(v).slice(0, 22) + "…" : String(v))}
            />
            <RTooltip
              {...getTooltipProps(darkMode)}
              formatter={(v) => [`${Number(v).toLocaleString()} ${unitLabel}`, totalLabel]}
            />
            <Bar dataKey="value" radius={[10, 10, 10, 10]} barSize={18}>
              {d.map((_, idx) => (
                <Cell key={idx} fill={chartPalette[idx % chartPalette.length]} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Box>
    </ChartCard>
  );
}

export default memo(HorizontalBarChart);
